import React, { useMemo, useEffect, useState } from "react";
import { Link } from "react-router-dom";
//Import Breadcrumb
import { Container } from "reactstrap";
import "react-toastify/dist/ReactToastify.css";
import {
  FetchUsers,
  handleBan,
  handleTestAccount,
  handleRemove,
  ExportUsers,
  SearchRecords,
} from "../../apis/User";
import { downloadListingsWorkbook } from "../Property/exportListingsWorkbook";
import { Search, Upload } from "lucide-react";
import Paginate from "../../components/shared/paginate";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";

const User = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.MANAGE_USERS);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [role, setRole] = useState("all");
  const [currentPage, setCurrentPage] = useState("1");
  const [totalPages, setTotalPages] = useState("1");
  const limit = "10";
  const [exporting, setExporting] = useState(false);

  const fetchUsers = async () => {
    try {
      const response = await FetchUsers(currentPage, limit);
      if (response.success) {
        setFilteredUsers(response.data.users);
        setTotalPages(Math.ceil(response.data.totalUsers / limit));
      } else {
        toast.error("Failed to fetch users. Please try again later.");
      }
    } catch (error) {
      toast.error("Failed to fetch users. Please try again later.");
    }
  };
  useEffect(() => {
    if (keyword || role !== "all") {
      searchUsers();
    } else {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, keyword, role]);

  const searchUsers = async () => {
    try {
      const response = await SearchRecords(keyword, currentPage, limit, role);
      if (response.success) {
        setFilteredUsers(response.data.results);
        setTotalPages(response.data.totalPages);
      } else {
        toast.error("Failed to search users. Please try again later.");
      }
    } catch (error) {
      toast.error("Failed to search users. Please try again later.");
    }
  };

  useEffect(() => {
    if (keyword || role !== "all") {
      setCurrentPage("1");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, role]);

  // useEffect(() => {
  //   searchUsers();
  // }, [role]);

  const handleRole = (e) => {
    setRole(e.target.value);
  };

  const columns = useMemo(
    () => [
      "Profile ID",
      "First name",
      "Last name",
      "Email",
      "Phone",
      "Role",
      "Gender",
      "Email Verified",
      "ELM Verified",
      "Account",
      "Created date",
      "View",
      "Action",
      "Delete",
    ],
    [],
  );

  const obscureEmail = (email) => {
    const [name, domain] = email.split("@");
    return `${name[0]}${new Array(name.length - 1).join("*")}${
      name[name.length - 1]
    }@${domain[0]}${new Array(domain.length - 1).join("*")}${
      domain[domain.length - 1]
    }`;
  };

  const handleUserTestAccount = (userId, isTestAccount) => {
    const nextValue = isTestAccount === true || isTestAccount === "true";
    handleTestAccount(userId, nextValue ? "true" : "false")
      .then(() => {
        setFilteredUsers((users) =>
          users.map((user) =>
            user._id === userId ? { ...user, isTestAccount: nextValue } : user,
          ),
        );
        toast.success(
          nextValue
            ? "Account marked as test. Dashboard user and listing counts skip it."
            : "Account marked as real. Dashboard counts include it again.",
        );
      })
      .catch(() => {
        toast.error("Could not update this account. Try again.");
      });
  };

  const accountTypeControl = (user) =>
    canManage ? (
      <select
        value={user.isTestAccount ? "true" : "false"}
        onChange={(event) => handleUserTestAccount(user._id, event.target.value)}
        className="p-2 border border-gray-500 dark:border-white rounded-md dark:bg-darkBg2"
        title="Test accounts stay on the site. Dashboard user and listing counts skip them."
      >
        <option value="false">Real</option>
        <option value="true">Test</option>
      </select>
    ) : (
      <span>{user.isTestAccount ? "Test" : "Real"}</span>
    );

  const handleUserBan = (userId, ban) => {
    handleBan(userId, ban).then((res) => {
      const tempUsers = filteredUsers.map((user) => {
        if (user._id === userId)
          return {
            ...user,
            userBanStatus: ban,
          };
        else return user;
      });
      setFilteredUsers(tempUsers);
      toast.success("User permission status updated");
    });
  };

  const handleUserRemove = (userId, remove) => {
    handleRemove(userId, remove).then((res) => {
      const tempUsers = filteredUsers.map((user) => {
        if (user._id === userId) {
          return {
            ...user,
            userDeletedAt: res.userDeletedAt,
            userDeletedBy: res.userDeletedBy,
          };
        } else return user;
      });
      setFilteredUsers(tempUsers);
      toast.success("User remove status updated");
    });
  };

  const exportUsers = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const response = await ExportUsers();
      const columns = response?.data?.columns;
      const rows = response?.data?.rows;
      if (!response?.success || !Array.isArray(columns) || !Array.isArray(rows)) {
        toast.error("Failed to export users.");
        return;
      }
      await downloadListingsWorkbook({
        columns,
        rows,
        sheetName: "Users",
        filename: `users-${new Date().toISOString().slice(0, 10)}.xlsx`,
      });
      toast.success(
        rows.length
          ? `Exported ${rows.length} user${rows.length === 1 ? "" : "s"}.`
          : "Export downloaded. No users to include.",
      );
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to export users.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container
          //  h-[calc(100vh-130px)]
          className="px-0 dark:bg-darkBg1 dark:text-[#c7d8eb]"
          fluid
        >
          <div className="p-6 flex flex-col gap-6 ">
            <div>
              <h1 className="text-2xl dark:text-[#c7d8eb]">Manage users</h1>
              <p className="text-sm text-gray-500 mt-1">
                Mark an account as Test to leave it, and its listings, out of
                the dashboard user and listing counts. The account stays usable
                everywhere else. Switch it back to Real and those counts include
                it again.
              </p>
            </div>
            <div className="lg:flex flex-col lg:border lg:border-b-0 border-gray-400 dark:border-white">
              <div className="border-x border-t lg:border-none border-gray-500 dark:border-white rounded-[6px_6px_0px_0px] w-full grid lg:grid-cols-[minmax(320px,_1fr)_160px_1fr] gap-4 p-4">
                <div className="relative border border-gray-500 dark:border-white rounded-md w-full">
                  <Search
                    size={16}
                    className="absolute left-2 bottom-1/2 translate-y-1/2"
                  />
                  <input
                    className="pl-8 pr-6 py-2 text-base rounded-md w-full dark:bg-darkBg2"
                    type="search"
                    placeholder="Search"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                  />
                </div>
                <select
                  value={role}
                  onChange={handleRole}
                  className="p-2 border border-gray-500 dark:border-white rounded-md dark:bg-darkBg2"
                >
                  <option value={"all"}>All</option>
                  <option value={"host"}>Host</option>
                  <option value={"guest"}>Guest</option>
                </select>
                <div className="flex lg:justify-self-end">
                  <button
                    type="button"
                    onClick={exportUsers}
                    disabled={exporting}
                    className="flex items-center gap-2 disabled:opacity-60"
                  >
                    {exporting ? "Exporting..." : "Export data into Excel"}
                    <div className="shrink-0 bg-white p-1">
                      <Upload className="" size={16} />
                    </div>
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto ">
                <div className="hidden lg:flex">
                  {columns.map((column, index) => (
                    <div
                      key={`column-${index}`}
                      className={`${
                        index === 0 ? "w-[12%]" : "w-[11%]"
                      } min-w-[130px] content-center border-y border-gray-500 dark:border-white break-words py-2 text-center font-semibold`}
                    >
                      {column}
                    </div>
                  ))}
                </div>
                {(filteredUsers &&
                  filteredUsers.length > 0 &&
                  filteredUsers.map((user, index) => (
                    <>
                      <div
                        key={`user-desktop-${index}`}
                        className="hidden lg:flex"
                      >
                        <div className="w-[12%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {user._id}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {user.firstName}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {user.lastName}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {obscureEmail(user.email)}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {user.mobileNumber || "-"}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center capitalize">
                          {user.roleId?.name || "-"}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center capitalize">
                          {user.gender || "-"}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          <span
                            className={
                              user.isEmailVerified
                                ? "text-green-600 font-semibold"
                                : "text-red-500"
                            }
                          >
                            {user.isEmailVerified ? "Yes" : "No"}
                          </span>
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          <span
                            className={
                              user.isElmVerified
                                ? "text-green-600 font-semibold"
                                : "text-red-500"
                            }
                          >
                            {user.isElmVerified ? "Yes" : "No"}
                          </span>
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {accountTypeControl(user)}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {user.createdAt?.toString()?.slice(0, 10)}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          <Link
                            className="text-secondaryColor"
                            to={`/profile-view/${user._id}`}
                          >
                            View
                          </Link>
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {canManage ? (
                            <select
                              value={user.userBanStatus}
                              onChange={(event) =>
                                handleUserBan(user._id, event.target.value)
                              }
                              className="p-2 border border-gray-500 dark:border-white rounded-md dark:bg-darkBg2"
                            >
                              <option value={null} disabled>
                                Select
                              </option>
                              <option value={true}>Prohibit</option>
                              <option value={false}>Permit</option>
                            </select>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {canManage ? (
                            (user.userDeletedAt && (
                              <button
                                onClick={() =>
                                  handleUserRemove(user._id, false)
                                }
                                className="text-secondaryColor"
                              >
                                Recover
                              </button>
                            )) || (
                              <button
                                onClick={() => handleUserRemove(user._id, true)}
                                className="text-secondaryColor"
                              >
                                Delete
                              </button>
                            )
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                      </div>
                      <div
                        key={`user-mobile-${index}`}
                        className="block lg:hidden mb-6 border-x border-y border-gray-500 dark:border-white"
                      >
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Profile ID</div>
                          <div>{user._id}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">First name</div>
                          <div>{user.firstName}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Last name</div>
                          <div>{user.lastName}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Email</div>
                          <div>{obscureEmail(user.email)}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Phone</div>
                          <div>{user.mobileNumber || "-"}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Role</div>
                          <div className="capitalize">
                            {user.roleId?.name || "-"}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Gender</div>
                          <div className="capitalize">{user.gender || "-"}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Email Verified</div>
                          <span
                            className={
                              user.isEmailVerified
                                ? "text-green-600 font-semibold"
                                : "text-red-500"
                            }
                          >
                            {user.isEmailVerified ? "Yes" : "No"}
                          </span>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">ELM Verified</div>
                          <span
                            className={
                              user.isElmVerified
                                ? "text-green-600 font-semibold"
                                : "text-red-500"
                            }
                          >
                            {user.isElmVerified ? "Yes" : "No"}
                          </span>
                        </div>
                        <div className="flex gap-2 justify-between items-center p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Account</div>
                          {accountTypeControl(user)}
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Created date</div>
                          <div>{user.createdAt?.toString()?.slice(0, 10)}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">View</div>
                          <button className="text-secondaryColor">View</button>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Action</div>
                          <div>
                            {canManage ? (
                              <select
                                value={user.userBanStatus}
                                onChange={(event) =>
                                  handleUserBan(user._id, event.target.value)
                                }
                                className="p-2 border border-gray-500 dark:border-white rounded-md dark:bg-darkBg2"
                              >
                                <option value={null} disabled>
                                  Select
                                </option>
                                <option value={true}>Prohibit</option>
                                <option value={false}>Permit</option>
                              </select>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-between p-2 ">
                          <div className="font-bold">Delete</div>
                          {canManage ? (
                            (user.userDeletedAt && (
                              <button
                                onClick={() =>
                                  handleUserRemove(user._id, false)
                                }
                                className="text-secondaryColor"
                              >
                                Recover
                              </button>
                            )) || (
                              <button
                                onClick={() => handleUserRemove(user._id, true)}
                                className="text-secondaryColor"
                              >
                                Delete
                              </button>
                            )
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                      </div>
                    </>
                  ))) || (
                  <div className="flex justify-center items-center h-20 border-x border-b border-gray-500 dark:border-white">
                    No matching records found.
                  </div>
                )}
              </div>
            </div>
            <Paginate
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
            />
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default User;
